/**
 * Sentiment Analysis — keyword-based sentiment scoring for events.
 *
 * Uses a curated dictionary of positive/negative terms weighted by
 * domain relevance (OSINT/geopolitical context).
 *
 * Per-event scoring: analyze title + description, produce normalized [-1, +1] score.
 * Per-country aggregate: average sentiment across all events for a country.
 *
 * Optional ML override: if browser AI provider is available, it can refine
 * the keyword score (Phase 2 integration point).
 */

import type { NormalizedEvent } from '@/sources/base-source';
import { getEventTimestamp } from './types';

export interface SentimentScore {
  eventId: string;
  score: number;        // -1.0 (very negative) to +1.0 (very positive)
  magnitude: number;    // 0-1 absolute strength of sentiment
  label: 'positive' | 'negative' | 'neutral';
  tier: 'keyword';      // Will support 'ml' in Phase 2
}

export interface CountrySentiment {
  countryCode: string;
  countryName: string;
  avgScore: number;
  eventCount: number;
  positiveCount: number;
  negativeCount: number;
  neutralCount: number;
}

/** Negative terms with weights. Higher weight = more strongly negative. */
const NEGATIVE_TERMS: Map<string, number> = new Map([
  // Conflict & violence
  ['war', 3], ['attack', 3], ['killed', 3], ['dead', 3], ['bomb', 3],
  ['explosion', 3], ['massacre', 3], ['genocide', 3], ['casualties', 3],
  ['missile', 2], ['strike', 2], ['invasion', 3], ['shelling', 2],
  ['airstrike', 3], ['troops', 1], ['military', 1], ['fighting', 2],
  ['battle', 2], ['conflict', 2], ['violence', 2], ['wounded', 2],
  ['hostage', 2], ['kidnap', 2], ['torture', 3], ['atrocit', 3],
  // Disaster & crisis
  ['earthquake', 2], ['tsunami', 3], ['flood', 2], ['hurricane', 2],
  ['cyclone', 2], ['famine', 3], ['drought', 2], ['wildfire', 2],
  ['disaster', 2], ['emergency', 2], ['crisis', 2], ['collapse', 2],
  ['catastroph', 3], ['devastat', 2], ['destroy', 2],
  // Political instability
  ['coup', 3], ['protest', 1], ['riot', 2], ['unrest', 2],
  ['sanction', 2], ['embargo', 2], ['threat', 2], ['tension', 1],
  ['escalat', 2], ['destabiliz', 2], ['authoritarian', 2],
  // Economic negative
  ['recession', 2], ['crash', 2], ['inflation', 1], ['deficit', 1],
  ['bankrupt', 2], ['default', 2], ['unemploy', 1], ['downturn', 2],
  // Humanitarian
  ['refugee', 1], ['displace', 1], ['starv', 2], ['suffer', 1],
  ['epidemic', 2], ['pandemic', 2], ['outbreak', 2],
  // Cyber
  ['breach', 2], ['hack', 2], ['ransomware', 2], ['malware', 2],
  ['cyberattack', 2], ['vulnerability', 1],
]);

/** Positive terms with weights. */
const POSITIVE_TERMS: Map<string, number> = new Map([
  // Peace & diplomacy
  ['peace', 3], ['ceasefire', 3], ['agreement', 2], ['treaty', 2],
  ['negotiat', 2], ['diplomat', 1], ['cooperat', 2], ['reconcil', 2],
  ['truce', 3], ['armistice', 3],
  // Recovery & growth
  ['recovery', 2], ['growth', 1], ['improve', 1], ['progress', 1],
  ['rebuild', 2], ['reconstruct', 2], ['restor', 2], ['stabiliz', 2],
  // Humanitarian positive
  ['aid', 1], ['relief', 1], ['rescue', 2], ['evacuate', 1],
  ['humanitarian', 1], ['donate', 1],
  // Economic positive
  ['surplus', 1], ['profit', 1], ['boom', 1], ['invest', 1],
  // Democracy & rights
  ['election', 1], ['democra', 1], ['freedom', 1], ['rights', 1],
  ['reform', 1], ['liberat', 2],
]);

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Known stem prefixes that should use startsWith matching. */
const STEM_SUFFIXES = new Set([
  'escalat', 'destabiliz', 'catastroph', 'atrocit', 'devastat',
  'negotiat', 'diplomat', 'cooperat', 'reconcil', 'restor', 'stabiliz',
  'reconstruct', 'evacuate', 'democra', 'liberat', 'unemploy', 'starv',
  'bankrupt',
]);

/**
 * Match a word against a dictionary term with word-boundary safety.
 * Short complete-word terms (<=4 chars) use exact match to avoid
 * false positives like "war" matching "software".
 * Stem prefixes use startsWith. Never use .includes().
 */
function matchTerm(word: string, term: string): boolean {
  if (STEM_SUFFIXES.has(term)) {
    return word.startsWith(term);
  }
  if (term.length <= 4) {
    return word === term;
  }
  return word.startsWith(term);
}

/**
 * Score sentiment for a single event based on keyword analysis.
 */
function scoreEvent(ev: NormalizedEvent): SentimentScore {
  const text = `${ev.title} ${ev.description ?? ''}`.toLowerCase();
  const words = text.split(/\s+/);

  let positiveWeight = 0;
  let negativeWeight = 0;

  for (const word of words) {
    // Check negative terms — use word-boundary-safe matching
    for (const [term, weight] of NEGATIVE_TERMS) {
      if (matchTerm(word, term)) {
        negativeWeight += weight;
        break; // One match per word
      }
    }
    // Check positive terms
    for (const [term, weight] of POSITIVE_TERMS) {
      if (matchTerm(word, term)) {
        positiveWeight += weight;
        break;
      }
    }
  }

  const totalWeight = positiveWeight + negativeWeight;
  let score: number;
  let magnitude: number;

  if (totalWeight === 0) {
    score = 0;
    magnitude = 0;
  } else {
    score = (positiveWeight - negativeWeight) / totalWeight;
    magnitude = Math.min(1, totalWeight / 15); // Normalize, cap at 1
  }

  let label: SentimentScore['label'];
  if (score > 0.1) label = 'positive';
  else if (score < -0.1) label = 'negative';
  else label = 'neutral';

  return {
    eventId: ev.id,
    score,
    magnitude,
    label,
    tier: 'keyword',
  };
}

/**
 * Analyze sentiment for a batch of events.
 * Returns per-event scores (only for events within the last 24 hours).
 */
export function analyzeSentiment(events: NormalizedEvent[]): SentimentScore[] {
  const cutoff = Date.now() - TWENTY_FOUR_HOURS_MS;
  const recent = events.filter(ev => getEventTimestamp(ev) >= cutoff);
  return recent.map(scoreEvent);
}

/**
 * Aggregate sentiment per country from event scores.
 */
export function aggregateCountrySentiment(
  events: NormalizedEvent[],
  scores: SentimentScore[],
): CountrySentiment[] {
  const scoreMap = new Map<string, SentimentScore>();
  for (const s of scores) scoreMap.set(s.eventId, s);

  const countryData = new Map<string, { scores: number[]; name: string; pos: number; neg: number; neutral: number }>();

  for (const ev of events) {
    const s = scoreMap.get(ev.id);
    if (!s) continue;

    const code = ev.location?.countryCode?.toUpperCase();
    if (!code) continue;

    let entry = countryData.get(code);
    if (!entry) {
      entry = { scores: [], name: ev.location?.country ?? code, pos: 0, neg: 0, neutral: 0 };
      countryData.set(code, entry);
    }

    entry.scores.push(s.score);
    if (s.label === 'positive') entry.pos++;
    else if (s.label === 'negative') entry.neg++;
    else entry.neutral++;
  }

  const results: CountrySentiment[] = [];
  for (const [code, data] of countryData) {
    if (data.scores.length === 0) continue;
    const avg = data.scores.reduce((a, b) => a + b, 0) / data.scores.length;
    results.push({
      countryCode: code,
      countryName: data.name,
      avgScore: Math.round(avg * 100) / 100,
      eventCount: data.scores.length,
      positiveCount: data.pos,
      negativeCount: data.neg,
      neutralCount: data.neutral,
    });
  }

  return results.sort((a, b) => a.avgScore - b.avgScore); // Most negative first
}
